import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';

import {
    NavigationContext,
    useAppContext,
    useComposition,
    useCompositionTemplates,
    useFrami,
} from '../context';
import { useTracking } from '../data/gtm';
import { lazyWithPreload } from '../utilities/lazy-with-preload';
import { useNavigateSearch } from '../utilities/helpers';
import { WorkspaceFloatingBottom } from '../components/studio/WorkspaceFloatingBottom';

const CheckoutStripe = lazyWithPreload(
    () => import(/* webpackChunkName: 'CheckoutStripe'  */ '../components/studio/drawer-content/CheckoutStripe')
);
const CompositionSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'CompositionSelect'  */ '../components/studio/drawer-content/CompositionSelect')
);
const TemplateFilters = lazyWithPreload(
    () => import(/* webpackChunkName: 'CompositionSelect'  */ '../components/studio/TemplateFilters')
);

const FavouriteSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'FavouriteSelect'  */ '../components/studio/drawer-content/FavouriteSelect')
);
const FilterSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'FilterSelect'  */ '../components/studio/drawer-content/FilterSelect')
);
const FramesFilter = lazyWithPreload(
    () => import(/* webpackChunkName: 'FramesFilter'  */ '../components/studio/drawer-content/FramesFilter')
);
const FrameSizeSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'FrameSizeSelect'  */ '../components/studio/drawer-content/FrameSizeSelect')
);
const FrameStyle = lazyWithPreload(
    () => import(/* webpackChunkName: 'FrameStyle'  */ '../components/studio/drawer-content/FrameStyle')
);
const Account = lazyWithPreload(
    () => import(/* webpackChunkName: 'Account'  */ '../components/studio/drawer-content/account/Account')
);
const PhotosSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'PhotosSelect'  */ '../components/studio/drawer-content/PhotosSelect')
);
const PriceFilter = lazyWithPreload(
    () => import(/* webpackChunkName: 'PriceFilter'  */ '../components/studio/drawer-content/PriceFilter')
);
const OrderStatus = lazyWithPreload(
    () => import(/* webpackChunkName: 'OrderStatus'  */ '../components/studio/drawer-content/OrderStatus')
);
const SizeSelect = lazyWithPreload(
    () => import(/* webpackChunkName: 'SizeSelect'  */ '../components/studio/drawer-content/SizeSelect')
);
const VideoScreen = lazyWithPreload(
    () => import(/* webpackChunkName: 'VideoScreen'  */ '../components/studio/drawer-content/VideoScreen')
);
const Voucher = lazyWithPreload(
    () => import(/* webpackChunkName: 'Voucher'  */ '../components/studio/drawer-content/Voucher')
);
const WallBackgroundEditor = lazyWithPreload(
    () => import(/* webpackChunkName: 'WallBackgroundEditor'  */ '../components/studio/drawer-content/WallBackgroundEditor')
);

const ProductEditor = lazyWithPreload(
    () => import(/* webpackChunkName: 'ProductEditor'  */ '../components/studio/drawer-content/ProductEditor')
);

const FramkyLogoNavbar = lazyWithPreload(
    () => import(/* webpackChunkName: 'FramkyLogoNavbar'  */ '../components/common/navigation/FramkyLogoNavbar')
);
const BottomBar = lazyWithPreload(() => import(/* webpackChunkName: 'BottomBar'  */ '../components/studio/BottomBar'));

export const VIDEO_SCREEN = 'VIDEO_SCREEN';
export const FRAME_COUNT_FILTER = 'FRAME_COUNT_FILTER';
export const PRICE_FILTER = 'PRICE_FILTER';
export const FILTER_DRAWER = 'FILTER_DRAWER';
export const FAVOURITE_DRAWER = 'FAVOURITE_DRAWER';
export const WALLSIZE_DRAWER = 'WALLSIZE_DRAWER';
export const CHECKOUT = 'CHECKOUT';
export const ORDER_STATUS = 'ORDER_STATUS';
export const VOUCHER = 'VOUCHER';
export const FRAMESIZE_DRAWER = 'FRAMESIZE_DRAWER';
export const LOGIN = 'LOGIN';
export const TEMPLATE_FILTER_DRAWER = 'TEMPLATE_FILTER_DRAWER';

export const IMAGE_DRAWER = 'IMAGE_DRAWER';
export const TEMPLATE_DRAWER = 'TEMPLATE_DRAWER';
export const FRAMESTYLE_DRAWER = 'FRAMESTYLE_DRAWER';
export const WALLBACKGROUND_DRAWER = 'WALLBACKGROUND_DRAWER';
export const PRODUCT_DRAWER = 'PRODUCT_DRAWER';

// Define action types
const QUEUE_DRAWER = 'QUEUE_DRAWER';
const DEQUEUE_DRAWER = 'DEQUEUE_DRAWER';
const RESET_QUEUE = 'RESET_QUEUE';

const IGNORE = 'ignore';
const ZOOM = 'zoom';

const VisibilityWrapper = ({ visible, children }) => {
    const style = {
      display: visible ? "block" : "none",
    };
  
    return <div style={style}>{children}</div>;
  };

const drawerReducer = (state, action) => {
    switch (action.type) {
        case QUEUE_DRAWER:
            return [...state, action.payload];
        case DEQUEUE_DRAWER:
            if (state.length > 0) {
                const newState = state.slice(0, -1);
                if (newState.length > 0) {
                    action.callback(newState[newState.length - 1]);
                } else {
                    action.callback(null);
                }
                return newState;
            }
            return state;
        case RESET_QUEUE:
            return [];
        default:
            return state;
    }
};

const TabContent = ({ activeTab }) => {
    const { isMobile } = useAppContext();
    return (
        <>
            <div style={{ display: activeTab === IMAGE_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%' }}>
                <PhotosSelect />
            </div>
            <div style={{ display: activeTab === TEMPLATE_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%'  }}>
                <CompositionSelect welcomeFlow={false} />
            </div>
            <div style={{ display: activeTab === FRAMESTYLE_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%'  }}>
                <FrameStyle />
            </div>
            <div style={{ display: activeTab === FRAMESIZE_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%'  }}>
                <FrameSizeSelect />
            </div>
            <div style={{ display: activeTab === WALLBACKGROUND_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%'  }}>
                <WallBackgroundEditor />
            </div>
            <div style={{ display: activeTab === PRODUCT_DRAWER ? 'block' : 'none', height: isMobile ? '' : '100%'  }}>
                <ProductEditor />
            </div>
        </>
    );
};

export function NavigationProvider({ children }) {
    const [drawerStack, dispatch] = useReducer(drawerReducer, []);

    const { currentFrami } = useFrami();
    const { currentComp, activeFrame, activeFrames, setSelectedFrame, setActiveFrame, setCurrentComp } =
        useComposition();
    const { setCurrentTemplate } = useCompositionTemplates();
    const { isMobile, smallHeightDevice } = useAppContext();

    const [floatingBottomContent, setFloatingBottomContent] = useState(null);
    const [floatingBottomHeight, setFloatingBottomHeight] = useState(0);
    const [floatingBottomVisible, setFloatingBottomVisible] = useState(!isMobile);

    const [drawerContent, setDrawerContent] = useState(null);
    const [drawerHeight, setDrawerHeight] = useState(0);
    const [drawerHeaderHeight, setDrawerHeaderHeight] = useState(0);
    const [tabContent, setTabContent] = useState(null);

    const [navbarContent, setNavbarContent] = useState(null);
    const [navbarHeight, setNavbarHeight] = useState(50);
    const [rightPanelWidth, setRightPanelWidth] = useState(0);

    const [toolbarHeight, setToolbarHeight] = useState(0);

    const [clickMode, setClickMode] = useState('zoom');
    const [frameStyleMode, setFrameStyleMode] = useState('basic');
    const [dragMode, setDragMode] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [openModelViewer, setOpenModelViewer] = useState(false);

    const [activeHint, setActiveHint] = useState(1);
    const [widthInputFocus, setWidthInputFocus] = useState(false);
    const [heightInputFocus, setHeightInputFocus] = useState(false);
    const { pushEvent } = useTracking();

    // const [cookies] = useCookies([
    //     'templateDrawerClickHintSeen',
    //     'photosUploadDrawerClickHintSeen',
    //     'templateClickHintSeen',
    //     'templateSizeClickHintSeen',
    //     'templateCategoryClickHintSeen',
    //     'photosUploadClickHintSeen',
    // ]);

    useEffect(() => {
        // CheckoutStripe.preload();
        CompositionSelect.preload();
        // FavouriteSelect.preload();
        // FilterSelect.preload();
        // FramesFilter.preload();
        // FrameSizeSelect.preload();
        // FrameStyle.preload();
        // Account.preload();
        PhotosSelect.preload();
        // PriceFilter.preload();
        // OrderStatus.preload();
        // SizeSelect.preload();
        // VideoScreen.preload();
        // Voucher.preload();
        FramkyLogoNavbar.preload();
        BottomBar.preload();
    }, []);



    const intl = useIntl();

    const navigate = useNavigateSearch();


    const queueDrawer = (drawer_id) => {
        if (drawerStack[drawerStack.length - 1] !== drawer_id) {
            dispatch({ type: QUEUE_DRAWER, payload: drawer_id });
        }
    };

    const resetDrawerQueue = () => {
        dispatch({ type: RESET_QUEUE });
        if (isMobile) {
            displayDrawer(null);
        }
    };

    const openDrawer = (drawer_id, resetQueue = false, frame = null, scrollToBottom = false) => {
        if (resetQueue) {
            resetDrawerQueue();
        }

        if (activeFrames) {
            setSelectedFrame(null);
        }

        displayDrawer(drawer_id, scrollToBottom);

        if (currentComp) {
            navigate(`/${currentFrami?.id}/${currentComp?.id}/`, {
                state: {
                    currentComp: currentComp,
                    drawerId: drawer_id,
                    activeFrame: frame,
                },
            });
        }

        queueDrawer(drawer_id);
    };

    const closeDrawer = useCallback(() => {
        dispatch({
            type: DEQUEUE_DRAWER,
            callback: (newActiveDrawer) => {
                displayDrawer(newActiveDrawer);
            },
        });
        if (currentComp && !currentComp?.is_voucher) {
            setSelectedFrame(null, 'zoom', true);
        }
    }, [currentComp]);

    const displayDrawer = useCallback(
        (drawer_id, scrollToBottom = false) => {
            setNavbarContent(<FramkyLogoNavbar />);
            if (drawer_id in [TEMPLATE_DRAWER, IMAGE_DRAWER, FRAMESTYLE_DRAWER]) {
                pushEvent('main_screen');
            }

            switch (drawer_id) {
                case VIDEO_SCREEN:
                    pushEvent('welcome_video_open', { version: 'V1' });
                    setDrawerContent(<VideoScreen />);
                    setTabContent(null);
                    // setFloatingBottomContent(
                    //     <FramiButton
                    //         dataCy="continue-button"
                    //         className="welcomeVideoContinueButton control uppercase"
                    //         variant="primary"
                    //         title={intl.formatMessage({ id: 'button.continue' })}
                    //         onClick={() => {
                    //             closeDrawer();
                    //             pushEvent('welcome_video_close', { method: 'click', value: 0.01, currency: 'PLN' });
                    //         }}
                    //         rightIcon={<MdChevronRight fill={uiColors.PRIMARY_100} />}
                    //     />
                    // );
                    break;
                case FRAME_COUNT_FILTER:
                    setDrawerContent(<FramesFilter />);
                    break;
                case PRICE_FILTER:
                    setDrawerContent(<PriceFilter />);
                    break;
                case IMAGE_DRAWER:
                    pushEvent('photo_select_screen');
                    setTabContent(<TabContent activeTab={IMAGE_DRAWER} />);
                    setFloatingBottomContent(<WorkspaceFloatingBottom />);
                    setDrawerContent(null);
                    break;
                case FILTER_DRAWER:
                    pushEvent('filter_screen');
                    setDrawerContent(<FilterSelect />);
                    break;
                case TEMPLATE_DRAWER:
                    pushEvent('template_screen');
                    setFloatingBottomContent(<WorkspaceFloatingBottom />);
                    setDrawerContent(null);
                    // setTabContent(<CompositionSelect welcomeFlow={false} />);
                    setTabContent(<TabContent activeTab={TEMPLATE_DRAWER} />);
                    break;
                case TEMPLATE_FILTER_DRAWER:
                    pushEvent('template_filter_screen');
                    setDrawerContent(<TemplateFilters />);
                    setTabContent(null);
                    setFloatingBottomContent(null);
                    break;
                case FAVOURITE_DRAWER:
                    pushEvent('favourite_screen');
                    setDrawerContent(<FavouriteSelect />);
                    break;
                case WALLSIZE_DRAWER:
                    pushEvent('wallsize_screen');
                    setDrawerContent(<SizeSelect />)
                    break;
                case FRAMESTYLE_DRAWER:
                    pushEvent('framestyle_screen');
                    setTabContent(<TabContent activeTab={FRAMESTYLE_DRAWER} />);
                    setDrawerContent(null);
                    setFloatingBottomContent(<WorkspaceFloatingBottom/>);
                    break;
                case FRAMESIZE_DRAWER:
                    pushEvent('framesize_screen');
                    setTabContent(<TabContent activeTab={FRAMESIZE_DRAWER} />);
                    setFloatingBottomContent(<WorkspaceFloatingBottom/>);
                    break;
                case WALLBACKGROUND_DRAWER:
                    pushEvent('wallbackground_screen');
                    setTabContent(<TabContent activeTab={WALLBACKGROUND_DRAWER} />);
                    setFloatingBottomContent(null);
                    break;
                case PRODUCT_DRAWER:
                    pushEvent('product_screen');
                    setTabContent(<TabContent activeTab={PRODUCT_DRAWER} />);
                    setFloatingBottomContent(null);
                    break;
                case CHECKOUT:
                    pushEvent('checkout_screen');
                    setDrawerContent(<CheckoutStripe />);
                    setTabContent(null);
                    break;
                case ORDER_STATUS:
                    pushEvent('orderstatus_screen');
                    setDrawerContent(<OrderStatus />);
                    setTabContent(null);
                    setFloatingBottomContent(null);
                    break;
                case VOUCHER:
                    pushEvent('voucher_screen');
                    setDrawerContent(null);
                    setTabContent(<Voucher />);
                    setFloatingBottomContent(<WorkspaceFloatingBottom />);
                    break;
                case LOGIN:
                    pushEvent('login_screen');
                    setDrawerContent(<Account />);
                    break;
                case null:
                    pushEvent('main_screen');
                    setNavbarContent(<FramkyLogoNavbar />);
                    setDrawerContent(null);
                    setFloatingBottomContent(<WorkspaceFloatingBottom />);

                    break;
                default:
                    console.error(`Invalid drawer: ${drawer_id}`);                    
                    return;
            }
            // if ([IMAGE_DRAWER, TEMPLATE_DRAWER].includes(drawer_id)) {
            //     setDrawerContent(
            //         <>
            //         <VisibilityWrapper visible={drawer_id === IMAGE_DRAWER}><PhotosSelect/></VisibilityWrapper>
            //         <VisibilityWrapper visible={drawer_id === TEMPLATE_DRAWER}><CompositionSelect welcomeFlow={false} /></VisibilityWrapper>
            //         </>
            //     );
            // }

        },
        [
            setDrawerContent,
            setFloatingBottomContent,
            setNavbarContent,
            activeFrame,
            intl,
            isMobile,
            smallHeightDevice,
            editMode,
        ]
    );

    const activeDrawer = useMemo(() => {
        const drawer = drawerStack.length > 0 ? drawerStack[drawerStack.length - 1] : null;
        return drawer;
    }, [drawerStack]);

    // This event is used by GTM web container to initialize tags, who doesn't trigger on page load due to consent not initialized
    useEffect(() => {
        pushEvent('init_tags');
    }, []);


    useEffect(() => {
        let mode;
        // if (activeDrawer === TEMPLATE_DRAWER && isMobile) {
        //     mode = IGNORE;
        // } else {
        mode = ZOOM;
        // }
        setClickMode(mode);
    }, [activeDrawer]);

    // useEffect(() => {
    //     if (
    //         [
    //             IMAGE_DRAWER,
    //             TEMPLATE_DRAWER,
    //             FAVOURITE_DRAWER,
    //             FILTER_DRAWER,
    //             FRAME_COUNT_FILTER,
    //             FRAMESTYLE_DRAWER,
    //             PRICE_FILTER,
    //             WALLSIZE_DRAWER,
    //         ].includes(activeDrawer) ||
    //         !activeDrawer
    //     ) {
    //         setDragMode(true);
    //     } else {
    //         setDragMode(false);
    //     }
    // }, [activeDrawer]);

    const setFloatingBottomContentWithLogging = (content) => {
        console.log("setFloatingBottomContentWithLogging", content);
        setFloatingBottomContent(content);
    }

    // useEffect(() => {
    //     smallHeightDevice && setNavbarContent(null);
    // }, [smallHeightDevice, activeDrawer]);

    useEffect(() => {
        setActiveHint(3);
        // if (!activeDrawer || TEMPLATE_DRAWER !== activeDrawer) {
        //     if (Math.floor(activeHint) === 1 && cookies.templateDrawerClickHintSeen) {
        //         setActiveHint(2);
        //     } else if (Math.floor(activeHint) === 2 && cookies.photosUploadDrawerClickHintSeen) {
        //         setActiveHint(3);
        //     }
        // } else if (activeDrawer === TEMPLATE_DRAWER) {
        //     if (
        //         !cookies.templateClickHintSeen &&
        //         isFeatureFlagAvailable &&
        //         posthog.getFeatureFlag('gesture-template-click') === 'show-gestures'
        //     ) {
        //         setActiveHint(1.1);
        //     } else if (
        //         !cookies.templateSizeClickHintSeen &&
        //         isFeatureFlagAvailable &&
        //         posthog.getFeatureFlag('gesture-composition-size-click') === 'show-gesture'
        //     ) {
        //         setActiveHint(1.2);
        //     } else if (
        //         !cookies.templateCategoryClickHintSeen &&
        //         isFeatureFlagAvailable &&
        //         posthog.getFeatureFlag('gesture-template-click') === 'show-gestures'
        //     ) {
        //         setActiveHint(1.3);
        //     } else {
        //         setActiveHint(2);
        //     }
        // }
    }, [
        // activeDrawer,
        // activeHint,
        // cookies.templateDrawerClickHintSeen,
        // cookies.photosUploadDrawerClickHintSeen,
        // cookies.templateClickHintSeen,
        // cookies.templateSizeClickHintSeen,
        // cookies.templateCategoryClickHintSeen,
        // cookies.photosUploadClickHintSeen,
        // isFeatureFlagAvailable
    ]);

    useEffect(() => {
        const handlePopState = (event) => {
            if (event.state?.usr) {
                if (event.state.usr?.currentComp) {
                    setCurrentComp(event.state.usr.currentComp, false);
                    setCurrentTemplate(null);
                }
                if (event.state.usr.drawerId) {
                    openDrawer(event.state.usr.drawerId);
                }
                setActiveFrame(event.state.usr?.activeFrame ?? null);
            }
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const value = {
        clickMode,
        frameStyleMode,
        setFrameStyleMode,
        dragMode,
        editMode,
        setEditMode,
        floatingBottomContent,
        setFloatingBottomContent: setFloatingBottomContentWithLogging,
        floatingBottomHeight,
        setFloatingBottomHeight,
        openModelViewer,
        setOpenModelViewer,
        tabContent,
        setTabContent,
        drawerContent,
        setDrawerContent,
        drawerHeight,
        setDrawerHeight,
        navbarContent,
        setNavbarContent,
        navbarHeight,
        setNavbarHeight,
        rightPanelWidth,
        setRightPanelWidth,
        toolbarHeight,
        setToolbarHeight,
        drawerStack,
        activeDrawer,
        drawerHeaderHeight,
        setDrawerHeaderHeight,
        openDrawer,
        closeDrawer,
        queueDrawer,
        resetDrawerQueue,
        activeHint,
        setActiveHint,
        widthInputFocus,
        heightInputFocus,
        setWidthInputFocus,
        setHeightInputFocus,
        floatingBottomVisible,
        setFloatingBottomVisible,
    };

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
